import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Box, Typography, ClickAwayListener, Tooltip } from "@mui/material";
import { styles } from "./stylesCustomFilter";
import theme from "@/theme";
import ArrowDawnSvg from "@/assets/svg/ArrowDawnSvg";
import LockSvg from "@/assets/svg/LockSvg";

interface FilterProps {
  label: string;
  isDropdown?: boolean;
  isActive?: boolean;
  width?: number;
  options?: string[];
  onSelect?: (value: string) => void;

  expandableOptions?: Record<string, { key?: string; label: string }[]>;
  selectedValue?: string;
  disabledOptions?: string[];
}

const CustomFilter = ({
  label,
  isDropdown = false,
  isActive = false,
  width,
  options = [],
  onSelect,
  expandableOptions,
  selectedValue,
  disabledOptions = [],
}: FilterProps) => {
  const [open, setOpen] = useState(false);
  const [subDropdownOpen, setSubDropdownOpen] = useState<string | null>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      });
    }
  }, [open]);

  const handleClick = () => {
    if (isDropdown) {
      setOpen((prev) => !prev);
      setSubDropdownOpen(null);
    } else {
      onSelect?.(label);
    }
  };

  const dropdownContent = (
    <Box
      sx={{
        ...styles.dropdown,
        position: "absolute",
        top: dropdownPos.top,
        left: dropdownPos.left,
      }}
      className="hide-scrollbar"
    >
      {options.map((option) => {
        const isExpandable = expandableOptions?.[option];
        const isDisabled = disabledOptions.includes(option);

        return (
          <Box
            key={option}
            sx={
              isDisabled
                ? {
                    ...styles.dropdownItem,
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }
                : label === option
                  ? styles.dropdownItemSelected
                  : styles.dropdownItem
            }
            onClick={() => {
              if (isDisabled) return;

              if (isExpandable) {
                setSubDropdownOpen(subDropdownOpen === option ? null : option);
              } else {
                onSelect?.(option);
                setOpen(false);
              }
            }}
          >
            <Typography sx={isDisabled ? styles.disabledText : styles.dropdownText}>
              {option}
            </Typography>

            {isDisabled ? (
              <Tooltip
                key={option}
                title={
                  isDisabled ? (
                    <Box sx={styles.tooltipWrapper}>
                      <Typography sx={styles.tooltipText}>
                        Available only for premium.
                      </Typography>
                    </Box>
                  ) : (
                    ""
                  )
                }
                placement="top"
                disableHoverListener={!isDisabled}
                componentsProps={{
                  tooltip: {
                    sx: styles.tooltipProps,
                  },
                }}
                PopperProps={{
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [10, -10],
                      },
                    },
                  ],
                }}
              >
                <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
                  <LockSvg width={16} height={16} />
                </Box>
              </Tooltip>
            ) : isExpandable ? (
              <Box
                sx={{
                  transform: "rotate(-90deg)",
                  marginLeft: "auto",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <ArrowDawnSvg color={theme.palette.custom.blueGray700} />
              </Box>
            ) : null}

            {isExpandable && subDropdownOpen === option && !isDisabled && (
              <Box sx={styles.subDropdown}>
                {expandableOptions[option].map((subOption) => (
                  <Box
                    key={subOption.key}
                    sx={
                      selectedValue === subOption.key
                        ? styles.dropdownItemSelected
                        : styles.dropdownItem
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      if (subOption.key !== undefined) {
                        onSelect?.(subOption.key);
                        setOpen(false);
                        setSubDropdownOpen(null);
                      }
                    }}
                  >
                    <Typography sx={styles.dropdownText}>{subOption.label}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );

  return (
    <ClickAwayListener
      onClickAway={() => {
        setOpen(false);
        setSubDropdownOpen(null);
      }}
    >
      <Box ref={buttonRef} sx={{ position: "relative", width: width ?? "auto", height: "32px" }}>
        <Box
          sx={{
            ...(isActive ? styles.activeButton : styles.inactiveButton),
            ...(width ? { width } : {}),
          }}
          onClick={handleClick}
        >
          <Typography sx={isActive ? styles.activeText : styles.inactiveText}>{label}</Typography>

          {isDropdown && (
            <Box
              sx={{
                ml: "0.5rem",
                display: "flex",
                alignItems: "center",
                transition: "transform 0.2s ease",
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
              }}
            >
              <ArrowDawnSvg color={isActive ? "#FFFFFF" : theme.palette.custom.blueGray700} />
            </Box>
          )}
        </Box>

        {isDropdown && open && createPortal(dropdownContent, document.body)}
      </Box>
    </ClickAwayListener>
  );
};

export default CustomFilter;
